import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import ExportMenu from "@/components/common/ExportMenu";

const CHANNELS = ["all", "meta", "google", "whatsapp", "email", "direct", "referral", "unknown"];
const TYPES = [
  { value: "all", label: "All types" },
  { value: "one_time", label: "One-time" },
  { value: "guardian", label: "Guardian" },
];
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

/** Server-side paginated donor ledger for the Diwali campaign - own search/
 * channel/type/page state, but reuses the page-level date range + refreshKey
 * so it reloads in lockstep with the cards/charts above. Search is debounced
 * and guarded against out-of-order responses (a slow earlier request can no
 * longer clobber a newer one). */
export default function DiwaliDonorsTable({ range, refreshKey }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [channel, setChannel] = useState("all");
  const [donationType, setDonationType] = useState("all");
  const [skip, setSkip] = useState(0);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = {
    start_date: range.start,
    end_date: range.end,
    search: search || undefined,
    channel: channel === "all" ? undefined : channel,
    donation_type: donationType === "all" ? undefined : donationType,
    skip,
    limit: PAGE_SIZE,
  };

  const fetchDonors = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get("/admin/diwali/donors", { params });
      if (requestId !== requestIdRef.current) return; // a newer request already landed
      setItems(data.items);
      setTotal(data.total);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end, search, channel, donationType, skip, refreshKey]);

  useEffect(() => {
    fetchDonors();
  }, [fetchDonors]);

  useEffect(() => {
    setSkip(0);
  }, [search, channel, donationType, range.start, range.end]);

  return (
    <div data-testid="diwali-donors-table">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <Input
            placeholder="Search name, email or phone"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="diwali-donors-search-input"
            className="h-10 w-56"
          />
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger className="h-10 w-40" data-testid="diwali-donors-channel-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CHANNELS.map((c) => (
                <SelectItem key={c} value={c} data-testid={`diwali-donors-channel-option-${c}`}>
                  {c === "all" ? "All channels" : c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={donationType} onValueChange={setDonationType}>
            <SelectTrigger className="h-10 w-40" data-testid="diwali-donors-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value} data-testid={`diwali-donors-type-option-${t.value}`}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <ExportMenu url="/admin/diwali/export" params={params} filename="amtm_diwali_donors" testId="diwali-donors-export" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
      ) : error ? (
        <div className="flex flex-col items-center gap-2 py-12 text-amtm-textMuted" data-testid="diwali-donors-error">
          <AlertTriangle className="h-6 w-6 text-destructive" />
          <p>Couldn't load donors. Try Refresh above.</p>
        </div>
      ) : items.length === 0 ? (
        <p className="text-amtm-textMuted text-center py-12" data-testid="diwali-donors-empty">No donors for this filter.</p>
      ) : (
        <div className="bg-amtm-surface border border-amtm-border rounded-xl overflow-x-auto" data-testid="diwali-donors-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-amtm-textMuted border-b border-amtm-border">
                <th className="p-2">Date</th>
                <th className="p-2">Donor</th>
                <th className="p-2">Amount</th>
                <th className="p-2">Type</th>
                <th className="p-2">Status</th>
                <th className="p-2">Channel</th>
                <th className="p-2">UTM Source</th>
                <th className="p-2">UTM Campaign</th>
                <th className="p-2">UTM Content</th>
                <th className="p-2">Landing Page</th>
                <th className="p-2">80G</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amtm-border">
              {items.map((d) => (
                <tr key={d.id} data-testid={`diwali-donor-row-${d.id}`}>
                  <td className="p-2 whitespace-nowrap">{formatDate(d.created_at)}</td>
                  <td className="p-2">
                    {d.name || "—"}
                    <br />
                    <span className="text-xs text-amtm-textMuted">{d.email}{d.phone ? ` · ${d.phone}` : ""}</span>
                  </td>
                  <td className="p-2 font-semibold text-amtm-success whitespace-nowrap">₹{Number(d.amount).toLocaleString("en-IN")}</td>
                  <td className="p-2 whitespace-nowrap">{d.donation_kind === "guardian" ? "Guardian" : "One-time"}</td>
                  <td className="p-2 capitalize">{d.status}</td>
                  <td className="p-2">{d.channel}</td>
                  <td className="p-2">{d.utm_source || "—"}</td>
                  <td className="p-2">{d.utm_campaign || "—"}</td>
                  <td className="p-2">{d.utm_content || "—"}</td>
                  <td className="p-2">{d.landing_page || "—"}</td>
                  <td className="p-2">{d.requested_80g ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && total > 0 && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-amtm-textMuted">
            {skip + 1}-{Math.min(skip + PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSkip((s) => Math.max(0, s - PAGE_SIZE))}
              disabled={skip === 0}
              data-testid="diwali-donors-prev-page"
              className="h-8 w-8 flex items-center justify-center rounded-lg border border-amtm-border text-amtm-textMuted disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setSkip((s) => s + PAGE_SIZE)}
              disabled={skip + PAGE_SIZE >= total}
              data-testid="diwali-donors-next-page"
              className="h-8 w-8 flex items-center justify-center rounded-lg border border-amtm-border text-amtm-textMuted disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
